/**
 * Project Hub view mode (card vs list), persisted in localStorage and synced
 * across components via a custom window event. Server preferences are updated
 * in the background for cross-device restore when localStorage is empty.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  fetchUserPreferences,
  patchUserPreferences,
  type ProjectHubViewMode
} from '../services/userPreferencesApi'

const STORAGE_KEY = 'wps:projectHub:viewMode'
const CUSTOM_EVENT = 'wps:projectHubViewModeChanged'

const isViewMode = (value: unknown): value is ProjectHubViewMode =>
  value === 'card' || value === 'list'

export function getProjectHubViewMode(): ProjectHubViewMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isViewMode(raw) ? raw : 'card'
  } catch {
    return 'card'
  }
}

function writeLocalStorage(value: ProjectHubViewMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // ignore quota / private mode errors
  }
}

function hasLocalStorageValue(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null
  } catch {
    return false
  }
}

export function setProjectHubViewMode(value: ProjectHubViewMode): void {
  writeLocalStorage(value)
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: { value } }))
  void patchUserPreferences({ projectHub: { viewMode: value } }).catch(() => {
    // Keep optimistic UI; preference is already in localStorage.
  })
}

export function useProjectHubViewMode(): [
  ProjectHubViewMode,
  (next: ProjectHubViewMode) => void
] {
  const [viewMode, setViewModeState] = useState<ProjectHubViewMode>(() => getProjectHubViewMode())

  useEffect(() => {
    const sync = () => setViewModeState(getProjectHubViewMode())

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) sync()
    }

    window.addEventListener(CUSTOM_EVENT, sync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CUSTOM_EVENT, sync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const seedFromServer = async () => {
      if (hasLocalStorageValue()) return

      try {
        const prefs = await fetchUserPreferences()
        if (cancelled) return
        const serverMode: ProjectHubViewMode =
          prefs.projectHub?.viewMode === 'list' ? 'list' : 'card'
        writeLocalStorage(serverMode)
        window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: { value: serverMode } }))
      } catch {
        // localStorage default ('card') is fine
      }
    }

    void seedFromServer()
    return () => {
      cancelled = true
    }
  }, [])

  const setViewMode = useCallback((next: ProjectHubViewMode) => {
    setProjectHubViewMode(next)
  }, [])

  return [viewMode, setViewMode]
}
